// src/avatars/index.js — 📷 profile photos UI (v126.7, spec Part 3 final).
// Loaded by BOTH apps as dist/avatars.js (globalName Avatars).
//
// The hydration model: any surface renders Avatars.chip(kind, id, name, size)
// — an INSTANT initials circle carrying data attributes — and a bounded 2s
// sweep replaces chips with the real headshot when one exists. The tiny
// /api/avatar/index (ids + ver only) says who has a photo; actual images
// fetch lazily per person and cache in localStorage keyed by ver, so a till
// renders 30 avatars from cache and fetches only what changed.

import { lsSetSafe } from '../storage/quota.js';   // v129.99 — quota self-healing (this cache was the main quota hog)

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  try { var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null; if (tk) headers['Authorization'] = 'Bearer ' + tk; } catch (e) {}
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).map(function (w) { return w[0]; }).slice(0, 2).join('').toUpperCase() || '?';
}
// Only a clean base64 JPEG data URL ever reaches innerHTML (the okPhoto rule).
function okJpeg(s) {
  return /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(String(s || '')) ? String(s) : '';
}
function myStore() {
  try { return (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null; } catch (e) { return null; }
}
function viewerQ() {
  try { var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null; if (tk) return ''; } catch (e) {}
  var st = myStore();
  return st ? 'viewer_store=' + encodeURIComponent(st) : '';
}

// ── The chip (instant initials; the sweep upgrades it) ───────────────────────
var AV_COLORS = ['#e17076', '#7bc862', '#65aadd', '#a695e7', '#ee7aae', '#6ec9cb'];
function avColor(s) {
  var h = 0; s = String(s || '');
  for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function chip(kind, id, name, size) {
  size = size || 28;
  if (id == null || (kind !== 'staff' && kind !== 'account')) return '';
  // v135.1: translate="no", because initials are letters from a name ("OK" for Omar Khan is not a word to translate).
  return '<span data-av-kind="' + kind + '" data-av-id="' + Number(id) + '" translate="no" ' +
    'style="display:inline-flex;align-items:center;justify-content:center;width:' + size + 'px;height:' + size + 'px;border-radius:50%;background:' + avColor(name) + ';color:#fff;font-size:' + Math.round(size * 0.4) + 'px;font-weight:800;overflow:hidden;flex-shrink:0;vertical-align:middle">' + esc(initials(name)) + '</span>';
}

// ── Caches + the sweep ───────────────────────────────────────────────────────
var _index = null;           // { 'staff:7': ver, 'account:5': ver }
var _indexAt = 0;
var _photos = {};            // key → data URL (memory)
var _fetching = {};

// v136.8 (the 16 Oct render audit + the Worker audit): a FAILED read was retried on every 2 s sweep, for ever.
// The index read left _indexAt at 0 on a 401 / 5xx, and a failed photo left nothing behind, so one device on a
// bad token or a sick Worker asked up to 1,800 times an hour (the likely cause of the 5x request spike on
// 16 Oct). Now a failure is stamped and treated as "no photo" (initials stay) until RETRY_FAIL_MS has passed.
var RETRY_FAIL_MS = 10 * 60 * 1000;
var _indexFailAt = 0;        // when the index read last failed (0 = it did not)
var _indexLoading = null;    // the index read in flight, shared (a draw's sweep and the timer's at once)
var _photoFailAt = {};       // key → when that person's photo read last failed

async function loadIndex() {
  if (_index && (Date.now() - _indexAt) < 10 * 60 * 1000) return _index;
  if (_indexFailAt && (Date.now() - _indexFailAt) < RETRY_FAIL_MS) return _index;   // backing off
  if (_indexLoading) return _indexLoading;
  _indexLoading = (async function () {
    try {
      var q = viewerQ();
      var res = await api('/api/avatar/index' + (q ? '?' + q : ''));
      if (!res.ok) { _indexFailAt = Date.now(); return _index; }
      var d = await res.json();
      if (d && d.ok) {
        _index = {};
        (d.index || []).forEach(function (r) {
          if (r.staff_id != null) _index['staff:' + r.staff_id] = r.ver || '1';
          if (r.account_id != null) _index['account:' + r.account_id] = r.ver || '1';
        });
        _indexAt = Date.now();
        _indexFailAt = 0;
      } else {
        _indexFailAt = Date.now();
      }
    } catch (e) { _indexFailAt = Date.now(); /* offline — initials everywhere is fine */ }
    return _index;
  })();
  try { return await _indexLoading; } finally { _indexLoading = null; }
}
async function photoFor(kind, id) {
  var key = kind + ':' + id;
  if (_photos[key] !== undefined) return _photos[key];
  var ver = _index && _index[key];
  if (!ver) return null;                                // index says no photo
  try {
    var stored = JSON.parse(localStorage.getItem('elcAv:' + key) || 'null');
    if (stored && stored.ver === ver && okJpeg(stored.photo)) { _photos[key] = stored.photo; return stored.photo; }
  } catch (e) {}
  if (_fetching[key]) return null;                      // one in-flight fetch per person
  if (_photoFailAt[key] && (Date.now() - _photoFailAt[key]) < RETRY_FAIL_MS) return null;   // v136.8: backing off
  _fetching[key] = true;
  try {
    var q = viewerQ();
    var res = await api('/api/avatar?' + (kind === 'staff' ? 'staff_id=' : 'account_id=') + id + (q ? '&' + q : ''));
    if (res.ok) {
      var d = await res.json();
      var p = okJpeg(d && d.photo);
      _photos[key] = p || null;
      delete _photoFailAt[key];
      // v129.99: lsSetSafe — on a full device this cache is the FIRST thing
      // evicted (photos refetch lazily); it must never brick other writes.
      if (p) { lsSetSafe('elcAv:' + key, JSON.stringify({ ver: ver, photo: p })); }
    } else {
      _photoFailAt[key] = Date.now();                   // v136.8: tried again after RETRY_FAIL_MS, not every sweep
    }
  } catch (e) { _photoFailAt[key] = Date.now(); /* retried after RETRY_FAIL_MS */ }
  _fetching[key] = false;
  return _photos[key] || null;
}
async function sweep() {
  if (typeof document === 'undefined') return;
  var nodes = document.querySelectorAll('[data-av-kind]:not([data-av-done])');
  if (!nodes.length) return;
  await loadIndex();
  if (!_index) return;
  for (var i = 0; i < nodes.length && i < 60; i++) {
    var el = nodes[i];
    var kind = el.getAttribute('data-av-kind'), id = el.getAttribute('data-av-id');
    var key = kind + ':' + id;
    if (!_index[key]) { el.setAttribute('data-av-done', '1'); continue; }   // no photo → initials stay
    var p = _photos[key] !== undefined ? _photos[key] : await photoFor(kind, id);
    if (p) {
      el.innerHTML = '<img src="' + p + '" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%">';
      el.setAttribute('data-av-done', '1');
    }
  }
}

// ── Capture: the headshot rules live ON the camera screen ────────────────────
// v126.22 — optional preselectId (Matt: "by clicking on your circle in clock
// in for stores"): the clock avatar passes the tapped person's id so their
// name is pre-picked; the PIN still proves it's really them.
var _adminTarget = null;   // { kind:'staff'|'account', id, name } when HO sets someone else's

function openCapture(preselectId) {
  _adminTarget = null;
  var old = document.getElementById('avCapModal'); if (old) old.remove();
  var accountMode = false;
  try { accountMode = !!((typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null); } catch (e) {}
  var staff = [];
  if (!accountMode) {
    // v127.42 (a store manager's report #2, task-close picker 29 Jul): the identity picker shows
  // home staff + TODAY'S ROSTERED COVER (getStoreRoster's v113.0 second arg),
  // so a supporter transferred to another store can act under their own name
  // at the store they're covering — the same rule as clock/checks/tally.
  // Falls back to home-only where the roster helper isn't loaded (chat.html).
  try {
    if (typeof currentStore !== 'undefined') {
      var _rosterDay = (typeof businessDayStr === 'function') ? businessDayStr() : null;
      if (typeof getStoreRoster === 'function') staff = getStoreRoster(currentStore, _rosterDay) || [];
      else if (typeof getStoreStaff !== 'undefined') staff = getStoreStaff(currentStore) || [];
    }
  } catch (e) { staff = []; }
  }
  var wrap = document.createElement('div');
  wrap.id = 'avCapModal';
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:9600;display:flex;align-items:center;justify-content:center;padding:16px';
  wrap.innerHTML = '<div style="background:var(--card);border-radius:16px;max-width:400px;width:100%;padding:18px;max-height:90vh;overflow-y:auto">' +
    '<div style="font-weight:900;font-size:16px">📷 Your profile photo</div>' +
    '<div style="font-size:12.5px;color:var(--ui-ink-2);margin:8px 0 12px;line-height:1.5;background:var(--surface-2);border-radius:10px;padding:10px 12px">' +
      '<b>A clear headshot of YOU</b> — face visible, no sunglasses or filters, <b>ideally in your ELC uniform or at your store</b>. Not a group photo, not a logo, not your pet. 🙂<br>' +
      '<span style="font-size:11.5px">Your photo shows to colleagues and Head Office wherever your name appears, and is deleted if you leave. Managers and HO can remove photos.</span></div>' +
    (accountMode ? '' :
      '<label style="font-size:12px;font-weight:700">Who are you?</label>' +
      '<select id="avCapWho" style="width:100%;border:1.5px solid var(--border-strong);border-radius:10px;padding:9px;font-size:13px;font-family:inherit;margin:4px 0 8px">' +
        '<option value="">— pick your name —</option>' +
        staff.map(function (s) { return '<option value="' + s.id + '" translate="no">' + esc(s.name) + '</option>'; }).join('') + '</select>' +
      '<label style="font-size:12px;font-weight:700">Your till PIN</label>' +
      '<input id="avCapPin" type="password" inputmode="numeric" maxlength="8" autocomplete="off" style="width:100%;box-sizing:border-box;border:1.5px solid var(--border-strong);border-radius:10px;padding:9px;font-size:13px;font-family:inherit;margin:4px 0 8px">') +
    '<input id="avCapFile" type="file" accept="image/*" capture="user" onchange="Avatars.filePicked(this)" style="display:none">' +
    '<div id="avCapPreview" style="text-align:center;margin:8px 0"></div>' +
    '<div id="avCapErr" style="color:var(--comms-err);font-size:12px;min-height:16px"></div>' +
    '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
      '<button onclick="document.getElementById(\'avCapFile\').click()" style="flex:1;border:none;border-radius:10px;padding:12px;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit">📸 Take / choose photo</button>' +
      '<button id="avCapSave" onclick="Avatars.saveCapture(this)" disabled style="flex:1;border:none;border-radius:10px;padding:12px;background:#8a94a6;color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit">Save</button></div>' +
    '<div style="display:flex;gap:8px;margin-top:8px">' +
      '<button onclick="Avatars.removeMine()" style="flex:1;border:none;background:none;color:var(--comms-err);font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">Remove my photo</button>' +
      '<button onclick="document.getElementById(\'avCapModal\').remove()" style="flex:1;border:none;background:none;color:var(--ui-ink-2);font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">Cancel</button></div></div>';
  wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
  if (preselectId && !accountMode) {
    var sel = document.getElementById('avCapWho');
    if (sel) { sel.value = String(preselectId); var pin = document.getElementById('avCapPin'); if (pin) pin.focus(); }
  }
}

// v126.22 (Matt: "in admin /people/directory and accounts") — HO sets ANY
// person's photo from the admin app. Same modal skeleton (filePicked reuses
// the ids), no name/PIN — the Bearer + the HO-only endpoint are the gate.
function openAdminSet(kind, id, name) {
  var old = document.getElementById('avCapModal'); if (old) old.remove();
  _adminTarget = { kind: kind === 'account' ? 'account' : 'staff', id: Number(id), name: name || '' };
  var wrap = document.createElement('div');
  wrap.id = 'avCapModal';
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:9600;display:flex;align-items:center;justify-content:center;padding:16px';
  wrap.innerHTML = '<div style="background:var(--card);border-radius:16px;max-width:400px;width:100%;padding:18px;max-height:90vh;overflow-y:auto">' +
    '<div style="font-weight:900;font-size:16px">📷 Photo for ' + esc(_adminTarget.name || (kind + ' #' + id)) + '</div>' +
    '<div style="font-size:12.5px;color:var(--ui-ink-2);margin:8px 0 12px;line-height:1.5;background:var(--surface-2);border-radius:10px;padding:10px 12px">A clear headshot — face visible, ideally in uniform or at the store. It shows wherever their name appears.</div>' +
    '<input id="avCapFile" type="file" accept="image/*" onchange="Avatars.filePicked(this)" style="display:none">' +
    '<div id="avCapPreview" style="text-align:center;margin:8px 0"></div>' +
    '<div id="avCapErr" style="color:var(--comms-err);font-size:12px;min-height:16px"></div>' +
    '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
      '<button onclick="document.getElementById(\'avCapFile\').click()" style="flex:1;border:none;border-radius:10px;padding:12px;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit">📸 Choose photo</button>' +
      '<button id="avCapSave" onclick="Avatars.saveCapture(this)" disabled style="flex:1;border:none;border-radius:10px;padding:12px;background:#8a94a6;color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit">Save</button></div>' +
    '<div style="display:flex;gap:8px;margin-top:8px">' +
      '<button onclick="Avatars.removeMine()" style="flex:1;border:none;background:none;color:var(--comms-err);font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">Remove their photo</button>' +
      '<button onclick="document.getElementById(\'avCapModal\').remove()" style="flex:1;border:none;background:none;color:var(--ui-ink-2);font-size:12px;font-weight:700;cursor:pointer;font-family:inherit">Cancel</button></div></div>';
  wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
}

var _pending = null;   // the cropped 256px JPEG data URL awaiting save
function filePicked(input) {
  var f = input && input.files && input.files[0];
  if (input) input.value = '';
  if (!f) return;
  var img = new Image();
  var url = URL.createObjectURL(f);
  img.onload = function () {
    try {
      // square centre-crop → 256px JPEG
      var side = Math.min(img.width, img.height);
      var sx = (img.width - side) / 2, sy = (img.height - side) / 2;
      var canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 256;
      canvas.getContext('2d').drawImage(img, sx, sy, side, side, 0, 0, 256, 256);
      URL.revokeObjectURL(url);
      _pending = canvas.toDataURL('image/jpeg', 0.8);
      var pv = document.getElementById('avCapPreview');
      if (pv) pv.innerHTML = '<img src="' + _pending + '" style="width:120px;height:120px;border-radius:60px;object-fit:cover;border:3px solid var(--elc-green,#009E3D)">';
      var save = document.getElementById('avCapSave');
      if (save) { save.disabled = false; save.style.background = 'var(--elc-green,#009E3D)'; }
    } catch (e) {
      URL.revokeObjectURL(url);
      var errEl = document.getElementById('avCapErr');
      if (errEl) errEl.textContent = 'Could not read that photo — try a different one.';
    }
  };
  img.onerror = function () { URL.revokeObjectURL(url); var errEl = document.getElementById('avCapErr'); if (errEl) errEl.textContent = 'Could not read that photo — try a different one.'; };
  img.src = url;
}

async function saveCapture(btn) {
  var errEl = document.getElementById('avCapErr');
  if (!_pending) { errEl.textContent = 'Take or choose a photo first.'; return; }
  var body = { photo: _pending };
  var endpoint = '/api/avatar';
  if (_adminTarget) {                                  // v126.22: HO sets someone else's
    endpoint = '/api/avatar/admin-set';
    body[_adminTarget.kind === 'account' ? 'account_id' : 'staff_id'] = _adminTarget.id;
  } else {
    var whoSel = document.getElementById('avCapWho');
    if (whoSel) {                                      // staff mode: name + PIN
      var who = whoSel.value, pin = (document.getElementById('avCapPin') || {}).value || '';
      if (!who) { errEl.textContent = 'Pick your name.'; return; }
      if (pin.length < 4) { errEl.textContent = 'Enter your till PIN.'; return; }
      body.auth_staff_id = Number(who); body.auth_pin = pin;
    }
  }
  btn.disabled = true;
  try {
    var res = await api(endpoint, { method: 'POST', body: JSON.stringify(body) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok) { errEl.textContent = (d && d.error) || 'Could not save the photo'; btn.disabled = false; return; }
    _pending = null; _index = null; _indexAt = 0; _photos = {}; _indexFailAt = 0; _photoFailAt = {};
    var m = document.getElementById('avCapModal'); if (m) m.remove();
    if (typeof showToast !== 'undefined') showToast('📷 Photo saved — it now shows wherever your name appears');
    document.querySelectorAll('[data-av-done]').forEach(function (el) { el.removeAttribute('data-av-done'); });
  } catch (e) { errEl.textContent = 'Could not save — check the connection'; btn.disabled = false; }
}

async function removeMine() {
  var body = {};
  if (_adminTarget) {                                  // v126.22: HO removes theirs
    body[_adminTarget.kind === 'account' ? 'account_id' : 'staff_id'] = _adminTarget.id;
  }
  var whoSel = document.getElementById('avCapWho');
  if (whoSel) {
    var who = whoSel.value, pin = (document.getElementById('avCapPin') || {}).value || '';
    var errEl = document.getElementById('avCapErr');
    if (!who || pin.length < 4) { if (errEl) errEl.textContent = 'Pick your name and enter your PIN to remove your photo.'; return; }
    body.auth_staff_id = Number(who); body.auth_pin = pin;
  }
  try {
    var res = await api('/api/avatar/remove', { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      _index = null; _indexAt = 0; _photos = {}; _indexFailAt = 0; _photoFailAt = {};
      var m = document.getElementById('avCapModal'); if (m) m.remove();
      if (typeof showToast !== 'undefined') showToast('Photo removed — back to initials');
    }
  } catch (e) { /* honest silence — the modal stays */ }
}

// ── Your own face on the "My photo" button (v131.78) ────────────────────────
//
// Matt: "where it says my photo in admin, once uploaded can it show your face?"
//
// It could not: the button was static markup that only ever opened the camera.
// Worth more than decoration - 23 of the 29 account holders have never uploaded
// one, and nothing on screen told them so. A camera icon looks the same whether
// you have a photo or not.
//
// So the swap is CONDITIONAL: your face once the index says you have one, the
// camera icon until then. That way the button answers "have I set mine?" just
// by looking at it.
async function mountMyPhoto() {
  if (typeof document === 'undefined') return;
  var btn = document.getElementById('myPhotoBtn');
  if (!btn) return;
  var me = null;
  try { me = (typeof currentAdmin !== 'undefined' && currentAdmin) ? currentAdmin : null; } catch (e) { return; }
  if (!me || me.id == null) return;
  var key = 'account:' + me.id;
  if (btn.getAttribute('data-av-me') === String(me.id)) return;   // already mounted for this person
  await loadIndex();
  if (!_index || !_index[key]) return;                             // no photo yet - camera icon stays
  btn.setAttribute('data-av-me', String(me.id));
  btn.innerHTML = chip('account', me.id, me.name, 18) +
    '<span style="margin-inline-start:6px;vertical-align:middle">My photo</span>';   // v135.1: logical side, mirrors for Arabic
}

// ── Boot: the sweep heartbeat (bounded — only unhydrated chips) ──────────────
var _booted = false;
function boot() {
  if (_booted || typeof document === 'undefined') return;
  _booted = true;
  setInterval(function () {
    // v136.8: a hidden page (another tab, a locked phone, a till's screen off) asks for nothing; the first
    // tick after it is shown again fills anything new.
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    sweep().catch(function () {});
    mountMyPhoto().catch(function () {});   // v131.78 - idempotent, guarded by data-av-me
  }, 2000);
}
if (typeof window !== 'undefined') boot();

// v136.8: tests start from empty caches (not used by the app).
function _resetCaches() { _index = null; _indexAt = 0; _photos = {}; _fetching = {}; _indexFailAt = 0; _indexLoading = null; _photoFailAt = {}; }

export { chip, sweep, mountMyPhoto, openCapture, openAdminSet, filePicked, saveCapture, removeMine, initials, loadIndex, _resetCaches };
